import { MenuOpen } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import * as React from 'react';
import { useMemo } from 'react';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { SummaryItem } from '~/components/MyWorkspace/SummaryItem';
import {
  SelectedEmployeeContext,
  SelectedEmployeeContextProps,
  defaultSelectedEmployeeContextProps,
  useSelectedEmployeeContext,
} from '~/components/SelectedEmployee';
import { useAppearance } from '~/data/appearance';
import { LeftSideMenuActions } from '~/features/Sidebars/LeftSideMenu/components/LeftSideMenuActions';
import { useLeftSideMenu } from '~/features/Sidebars/LeftSideMenu/useLeftSideMenu';
import { useSelectedEmployeeDetails } from '~/hooks/employee/useSelectedEmployeeDetails';
import { legacyColors } from '~/legacyTheme';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';
import { employeeModulesAccessRoles } from '~/scenes/PortalViews/Views/EmployeeViews/data';
import { mergeSxProps } from '~/utils/helpers/sxProps';

import { LeftSideMenuLinks } from './components/LeftSideMenuLinks';
import { useSxStyles } from './useStyles';

export interface Props {
  open: boolean;
  routerLink?: boolean;
  handleDrawerOpen?: () => void;
  handleDrawerClose?: () => void;
  iframeMode?: boolean;
}

export const LeftSideMenu = (props: Props) => {
  const loggedInEmployeeId = useLoggedInEmployeeId();
  const parentSelectedEmployeeContext = useSelectedEmployeeContext();

  const loggedInEmployeeDetails = useSelectedEmployeeDetails({
    employeeId:
      !parentSelectedEmployeeContext.selectedEmployeeDetails.hasRoleAccessOverEmployee(
        employeeModulesAccessRoles.people.all,
      )
        ? loggedInEmployeeId
        : undefined,
  });

  const leftSideMenuSelectedEmployeeContext: SelectedEmployeeContextProps =
    useMemo(() => {
      const { hasRoleAccessOverEmployee } =
        parentSelectedEmployeeContext.selectedEmployeeDetails;

      // Use Selected Employee
      if (hasRoleAccessOverEmployee(employeeModulesAccessRoles.people.all)) {
        return parentSelectedEmployeeContext;
      }

      // Use Logged In User
      const {
        isDataInSync,
        isLoading: isLoadingLoggedInInfo,
        ...detailsRest
      } = loggedInEmployeeDetails;

      return {
        ...defaultSelectedEmployeeContextProps,
        ...detailsRest,
        selectedEmployeeId: loggedInEmployeeId,
        isSelectedEmployeeLoggedInUser: true,
        isLoading: isLoadingLoggedInInfo || !isDataInSync,
      };
    }, [
      parentSelectedEmployeeContext,
      loggedInEmployeeId,
      loggedInEmployeeDetails,
    ]);

  return (
    <SelectedEmployeeContext.Provider
      value={leftSideMenuSelectedEmployeeContext}
    >
      <LeftSideMenuContent {...props} />
    </SelectedEmployeeContext.Provider>
  );
};

export const LeftSideMenuContent: React.FC<Props> = ({
  open,
  routerLink,
  handleDrawerClose,
  handleDrawerOpen,
  iframeMode = false,
}) => {
  const { employeeInfo } = useLeftSideMenu();

  const { customAppearance, isLoading, isEnabled, hasData } = useAppearance();

  const { brandColors, hasBranding } = useMemo(() => {
    const hasBranding =
      isEnabled &&
      customAppearance.fLeftSideMenuColors &&
      customAppearance.fLeftSideMenuColors.fBackgroundColor !== '';

    const defaultBranding = {
      backgroundColor: grey[300],
      color: legacyColors.black,
    };

    const resolveColor = (color: string | undefined, defaultColor: string) => {
      return !!color ? color : defaultColor;
    };

    return {
      hasBranding,
      brandColors: isEnabled
        ? {
            backgroundColor: resolveColor(
              customAppearance?.fLeftSideMenuColors?.fBackgroundColor,
              defaultBranding.backgroundColor,
            ),
            color: resolveColor(
              customAppearance?.fLeftSideMenuColors?.fIconsColor,
              defaultBranding.color,
            ),
          }
        : defaultBranding,
    };
  }, [customAppearance, isEnabled]);

  const sxStyles = useSxStyles();

  const sidebarToggleEnabled = !!handleDrawerClose && !!handleDrawerOpen;
  const sidebarOpen = sidebarToggleEnabled ? open : true;
  const handleSidebarToggle = () => {
    if (sidebarToggleEnabled) {
      if (sidebarOpen) {
        handleDrawerClose();
      } else {
        handleDrawerOpen();
      }
    }
  };

  if (isLoading || !hasData) {
    return <LoadingView />;
  }

  return (
    <>
      <Box
        sx={mergeSxProps(
          sxStyles.drawer,
          sidebarOpen ? sxStyles.drawerOpen : sxStyles.drawerClose,
          { backgroundColor: brandColors.backgroundColor },
        )}
      >
        <Box sx={sxStyles.iconButtonDiv}>
          {sidebarToggleEnabled && (
            <IconButton
              onClick={handleSidebarToggle}
              sx={mergeSxProps(
                sxStyles.iconButton,
                hasBranding
                  ? {
                      color: brandColors.color,
                      '&:hover': {
                        backgroundColor: `${brandColors.color}40`,
                      },
                    }
                  : { backgroundColor: legacyColors.grey2 },
              )}
              size="large"
            >
              {sidebarOpen ? <MenuOpen /> : <MenuOpen sx={sxStyles.flipIcon} />}
            </IconButton>
          )}
        </Box>
        <Box
          sx={mergeSxProps(
            sxStyles.menuContent,
            sidebarOpen ? sxStyles.menuContentOpen : sxStyles.menuContentClose,
          )}
        >
          <SummaryItem
            avatarProps={{
              alt: employeeInfo?.firstName,
              src: employeeInfo?.profilePic,
            }}
            listItemProps={{
              disableGutters: true,
            }}
            customSxStyles={{
              listItemRoot: sxStyles.employeeMainData,
              listItemTextPrimary: sxStyles.font,
              avatar: sxStyles.avatar,
            }}
            primaryElement={
              <div>
                <Typography
                  variant="inherit"
                  noWrap={true}
                  sx={{
                    color: brandColors.color,
                  }}
                  component={'div'}
                >
                  <div>{employeeInfo?.firstName}</div>
                  <div>{employeeInfo?.lastName}</div>
                </Typography>
              </div>
            }
          />
          <LeftSideMenuLinks
            target={(iframeMode && '_parent') || '_self'}
            routerLink={routerLink}
            color={brandColors.color}
          />
          <LeftSideMenuActions />
        </Box>
      </Box>
    </>
  );
};
